<template>
  <Switch
    v-if="!store?.version || store?.version < 2"
    class="switch-ghl"
    :label="t('survey.footer.enableNewFooter')"
    :value="store?.builderConfig?.newFooter"
    @update:value="handleNewFooter"
  />
  <section
    v-if="store?.builderConfig?.newFooter"
    class="flex flex-col items-center justify-center gap-[20px] self-stretch"
  >
    <div class="flex flex-col gap-2">
      <UITextXsMedium>{{ t("survey.footer.footerType") }}</UITextXsMedium>
      <div class="flex items-start gap-4 self-stretch">
        <div class="footer-card-saperator" @click="handleFooterStyle('stickyFooter', true)">
          <div class="footer-card" :class="[currentFooterStyle?.stickyFooter ? 'active' : '']">
            <StickToCard></StickToCard>
          </div>
          <div>{{ t("survey.footer.stickToCard") }}</div>
        </div>
        <div class="footer-card-saperator" @click="handleFooterStyle('stickyFooter', false)">
          <div class="footer-card" :class="[!currentFooterStyle?.stickyFooter ? 'active' : '']">
            <StickToPage></StickToPage>
          </div>
          <div>{{ t("survey.footer.stickToPage") }}</div>
        </div>
      </div>
    </div>
    <ColorInput
      :label="t('survey.footer.backgroundFill')"
      :color="currentFooterStyle?.backgroundFill"
      @update:color="handleFooterStyle('backgroundFill', $event)"
    />
    <div class="grid grid-cols-2 gap-4">
      <PixelNumber
        :label="t('survey.footer.footerHeight')"
        :value="parseFloat(currentFooterStyle?.height)"
        :min="50"
        :max="200"
        @update:value="handleFooterStyle('height', $event)"
      />
      <FontFamilyInput
        :label="t('common.fontFamily')"
        :value="currentFooterStyle?.fontFamily"
        @update:value="handleFooterStyle('fontFamily', $event)"
      ></FontFamilyInput>
      <PixelNumber
        :label="t('common.size')"
        :value="parseFloat(currentFooterStyle?.fontSize)"
        @update:value="handleFooterStyle('fontSize', $event)"
      />
      <div class="flex flex-col items-start gap-2 self-stretch">
        <UITextXsMedium>{{ t("common.weight") }}</UITextXsMedium>
        <UISelect
          id="font-weight"
          :value="currentFooterStyle?.fontWeight"
          :options="fontWeights"
          :placeholder="t('common.selectOne')"
          @change="
            handleFooterStyle('fontWeight', $event);
            store.addToHistory();
          "
        />
      </div>
    </div>
    <section
      class="message-styling flex flex-col items-start justify-center gap-4 self-stretch p-3"
    >
      <Switch
        :label="t('survey.footer.enableProgressBar')"
        :value="currentFooterStyle?.enableProgressBar"
        :disabled="store.deviceMode !== 'mobile' && currentFooterStyle?.stickyFooter"
        @update:value="handleFooterStyle('enableProgressBar', $event)"
      />
      <div
        v-if="currentFooterStyle?.enableProgressBar"
        class="flex flex-col justify-items-start gap-4 self-stretch"
      >
        <div
          v-if="store.deviceMode !== 'mobile'"
          class="flex items-center justify-between py-2 text-gray-700"
        >
          {{ t("common.themes.themes") }}
        </div>

        <ThemeInput
          v-if="store.deviceMode !== 'mobile'"
          :theme-selected="store?.footerStyle?.theme"
          @update-theme="handleTheme"
        />
        <div v-if="store.deviceMode === 'mobile'" class="flex flex-col gap-3">
          <ColorInput
            :label="t('survey.footer.stepTextColor')"
            :color="currentProgressBarStyle?.textColor"
            @update:color="handleProgressBarStyle('textColor', $event)"
          />
          <div class="grid grid-cols-2 gap-3">
            <ColorInput
              :label="t('survey.footer.progressBarFill')"
              :compact="true"
              :color="currentProgressBarStyle?.completeFillColor"
              @update:color="handleProgressBarStyle('completeFillColor', $event)"
            />
            <ColorInput
              :label="t('survey.footer.progressBarInactive')"
              :compact="true"
              :color="currentProgressBarStyle?.inactiveFillColor"
              @update:color="handleProgressBarStyle('inactiveFillColor', $event)"
            />
          </div>
        </div>
        <div v-else class="grid grid-cols-2 gap-3">
          <ColorInput
            :label="t('survey.footer.textColor')"
            :compact="true"
            :color="currentProgressBarStyle?.textColor"
            @update:color="handleProgressBarStyle('textColor', $event)"
          />
          <ColorInput
            :label="t('survey.footer.fillComplete')"
            :compact="true"
            :color="currentProgressBarStyle?.completeFillColor"
            @update:color="handleProgressBarStyle('completeFillColor', $event)"
          />
          <ColorInput
            :label="t('survey.footer.fillInactive')"
            :compact="true"
            :color="currentProgressBarStyle?.inactiveFillColor"
            @update:color="handleProgressBarStyle('inactiveFillColor', $event)"
          />
          <ColorInput
            :label="t('common.style.borderColor')"
            :compact="true"
            :color="store?.footerStyle?.progressBarStyle?.borderColor"
            @update:color="handleProgressBarStyle('borderColor', $event)"
          />
          <PixelNumber
            :label="t('common.style.borderWidth')"
            :value="parseFloat(store?.footerStyle?.progressBarStyle?.borderWidth)"
            @update:value="handleProgressBarStyle('borderWidth', $event)"
          />
          <PixelNumber
            :label="t('common.cornerRadius')"
            :value="parseFloat(store?.footerStyle?.progressBarStyle?.borderRadius)"
            @update:value="handleProgressBarStyle('borderRadius', $event)"
          />
        </div>
      </div>
    </section>
    <section
      class="message-styling flex flex-col items-start justify-center gap-4 self-stretch p-3"
    >
      <UITextSmMedium>
        {{ store.deviceMode === "mobile" ? t("survey.footer.button") : t("survey.footer.buttons") }}
      </UITextSmMedium>
      <div v-if="store.deviceMode !== 'mobile'" class="w-100 grid grid-cols-2 gap-3">
        <div class="flex flex-col items-start gap-2 self-stretch">
          <UITextXsMedium class="flex"
            >{{ t("survey.footer.buttonType") }}
            <UITooltip :style="{ maxWidth: '300px' }" trigger="hover" placement="top">
              <template #trigger>
                <InfoCircleIcon class="ml-1 h-4 w-4 text-gray-400"></InfoCircleIcon>
              </template>
              {{ t("common.fontFamilyTooltip") }}
            </UITooltip></UITextXsMedium
          >
          <UISelect
            id="button-type"
            :value="store?.footerStyle?.buttonStyle?.buttonType"
            :options="buttonTypes"
            :placeholder="t('common.selectOne')"
            @change="
              handleButtonStyle('buttonType', $event);
              store.addToHistory();
            "
          />
        </div>
        <div class="flex flex-col items-start gap-2">
          <UITextXsMedium class="flex"
            >{{ t("survey.footer.position") }}
            <UITooltip :style="{ maxWidth: '300px' }" trigger="hover" placement="top">
              <template #trigger>
                <InfoCircleIcon class="ml-1 h-4 w-4 text-gray-400"></InfoCircleIcon>
              </template>
              {{ t("common.fontFamilyTooltip") }}
            </UITooltip></UITextXsMedium
          >
          <UISelect
            id="position"
            :value="store?.footerStyle?.buttonStyle?.position"
            :options="positions"
            :placeholder="t('common.selectOne')"
            @change="
              handleButtonStyle('position', $event);
              store.addToHistory();
            "
          />
        </div>
      </div>
      <ColorInput
        :label="t('survey.footer.textColor')"
        :color="currentButtonStyle?.fontColor"
        @update:color="handleButtonStyle('fontColor', $event)"
      />
      <UIForm
        v-if="store.deviceMode !== 'mobile'"
        id="button-form"
        ref="root"
        :model="buttonFormValue"
        :rules="rules"
      >
        <div class="grid grid-cols-2">
          <UIFormItem path="prevBtnText">
            <div class="flex flex-col items-start gap-2 self-stretch">
              <UITextXsMedium
                >{{ t("survey.footer.previousText")
                }}<span class="ml-1 text-red-600">*</span></UITextXsMedium
              >
              <UIInput
                id="prevBtnText"
                v-model="buttonFormValue.prevBtnText"
                class="right-zero"
                :placeholder="t('survey.footer.placeholders.prev')"
                :disabled="store?.footerStyle?.buttonStyle?.buttonType === 'onlyArrow'"
                @blur="store.addToHistory()"
              />
            </div>
          </UIFormItem>
          <ColorInput
            :label="t('survey.footer.fillPrevious')"
            :color="store?.footerStyle?.buttonStyle?.backButtonBgColor"
            :flat-radius="true"
            :compact="true"
            @update:color="handleButtonStyle('backButtonBgColor', $event)"
          />
        </div>
        <div class="grid grid-cols-2">
          <UIFormItem path="nextBtnText">
            <div class="flex flex-col items-start gap-2 self-stretch">
              <UITextXsMedium
                >{{ t("survey.footer.nextText")
                }}<span class="ml-1 text-red-600">*</span></UITextXsMedium
              >
              <UIInput
                id="nextBtnText"
                v-model="buttonFormValue.nextBtnText"
                class="right-zero"
                :placeholder="t('survey.footer.placeholders.next')"
                :disabled="store?.footerStyle?.buttonStyle?.buttonType === 'onlyArrow'"
                @blur="store.addToHistory()"
              />
            </div>
          </UIFormItem>
          <ColorInput
            :label="t('survey.footer.fillNext')"
            :color="store?.footerStyle?.buttonStyle?.nextButtonBgColor"
            :flat-radius="true"
            :compact="true"
            @update:color="handleButtonStyle('nextButtonBgColor', $event)"
          />
        </div>
        <div class="grid grid-cols-2">
          <UIFormItem path="submitBtnText">
            <div class="flex flex-col items-start gap-2 self-stretch">
              <UITextXsMedium
                >{{ t("survey.footer.submitText")
                }}<span class="ml-1 text-red-600">*</span></UITextXsMedium
              >
              <UIInput
                id="submitBtnText"
                v-model="buttonFormValue.submitBtnText"
                class="right-zero"
                :placeholder="t('survey.footer.placeholders.submit')"
                @blur="store.addToHistory()"
              />
            </div>
          </UIFormItem>
          <ColorInput
            :label="t('survey.footer.fillSubmit')"
            :color="currentButtonStyle?.submitButtonBgColor"
            :flat-radius="true"
            :compact="true"
            @update:color="handleButtonStyle('submitButtonBgColor', $event)"
          />
        </div>
      </UIForm>
      <UIForm
        v-else
        id="button-form-mobile"
        ref="rootMobile"
        :model="{ submitBtnText: mobileSubmitBtnText }"
        :rules="mobileRules"
        class="w-full"
      >
        <div class="grid grid-cols-2">
          <UIFormItem path="submitBtnText">
            <div class="flex flex-col items-start gap-2 self-stretch">
              <UITextXsMedium
                >{{ t("survey.footer.submitText")
                }}<span class="ml-1 text-red-600">*</span></UITextXsMedium
              >
              <UIInput
                id="submitBtnTextMobile"
                v-model="mobileSubmitBtnText"
                class="right-zero"
                :placeholder="t('survey.footer.placeholders.submit')"
                @blur="handleMobileSubmitText"
              />
            </div>
          </UIFormItem>
          <ColorInput
            :label="t('survey.footer.submitColorMobile')"
            :color="currentButtonStyle?.submitButtonBgColor"
            :flat-radius="true"
            :compact="true"
            @update:color="handleButtonStyle('submitButtonBgColor', $event)"
          />
        </div>
      </UIForm>
    </section>
  </section>
</template>

<script setup lang="ts">
import StickToCard from "@/assets/icons/stick-to-card.svg";
import StickToPage from "@/assets/icons/stick-to-page.svg";
import { useAppStore } from "@/store/app";
import { PERCENTAGE_BAR_DEFAULT_TEXTCOLOR, STEPS_DEFAULT_TEXTCOLOR } from "@/util/constants";
import { InfoCircleIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIForm,
  UIFormItem,
  UIInput,
  UISelect,
  UITextSmMedium,
  UITextXsMedium,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { Ref, computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ColorInput from "../Elements/ColorInput.vue";
import FontFamilyInput from "../Elements/FontFamilyInput.vue";
import PixelNumber from "../Elements/PixelNumber.vue";
import Switch from "../Elements/Switch.vue";
import ThemeInput from "../ThemeInput.vue";

const { t } = useI18n();
const store = useAppStore();

const fontWeights = Array.from({ length: 9 }, (_, index) => {
  const weight = (index + 1) * 100;
  return {
    label: weight.toString(),
    value: weight.toString(),
  };
});

const buttonTypes = computed(() => [
  {
    label: t("survey.footer.buttonTypes.textAndArrow"),
    value: "textAndArrow",
  },
  {
    label: t("survey.footer.buttonTypes.onlyText"),
    value: "onlyText",
  },
  {
    label: t("survey.footer.buttonTypes.onlyArrow"),
    value: "onlyArrow",
    disabled: !store.enableSaveButton,
  },
]);

const positions = computed(() => [
  {
    label: t("common.left"),
    value: "left",
  },
  {
    label: t("survey.footer.positions.leftAndRight"),
    value: "leftAndRight",
  },
  {
    label: t("common.right"),
    value: "right",
  },
]);

// Computed properties for current footer style based on device mode
const currentFooterStyle = computed(() => {
  return store.deviceMode === "mobile" ? store.mobileFooterStyle : store.footerStyle;
});

const currentProgressBarStyle = computed(() => {
  return currentFooterStyle.value?.progressBarStyle;
});

const currentButtonStyle = computed(() => {
  return currentFooterStyle.value?.buttonStyle;
});

const mobileSubmitBtnText = ref(
  store.mobileFooterStyle?.buttonStyle?.submitBtnText || t("survey.footer.placeholders.submit")
);

const handleNewFooter = (val: boolean) => {
  store.builderConfig.newFooter = val;
  store.anyUnsavedChanges = true;
};

const handleFooterStyle = (property, value) => {
  const isMobile = store.deviceMode === "mobile";
  const targetStyle = isMobile ? store.mobileFooterStyle : store.footerStyle;

  if (property === "stickyFooter") {
    targetStyle.stickyFooter = value;
    if (!isMobile) {
      targetStyle.enableProgressBar = !value;
    }
  } else if (property === "height") {
    targetStyle.height = value || 50;
  } else if (property === "fontFamily") {
    if (!store.fontCollection.includes(value)) {
      store.fontCollection.push(value);
    }
    targetStyle.fontFamily = value;
  } else {
    targetStyle[property] = value;
  }
  store.anyUnsavedChanges = true;
};

const handleTheme = (val: string) => {
  store.footerStyle.theme = val;
  const progressBarStyle = store.footerStyle?.progressBarStyle;
  if (progressBarStyle) {
    if (
      val === "percentageBar" &&
      progressBarStyle.textColor === PERCENTAGE_BAR_DEFAULT_TEXTCOLOR
    ) {
      progressBarStyle.textColor = STEPS_DEFAULT_TEXTCOLOR;
    } else if (val === "steps" && progressBarStyle.textColor === STEPS_DEFAULT_TEXTCOLOR) {
      progressBarStyle.textColor = PERCENTAGE_BAR_DEFAULT_TEXTCOLOR;
    }
  }
  store.anyUnsavedChanges = true;
};

const handleProgressBarStyle = (property, value) => {
  const isMobile = store.deviceMode === "mobile";
  const targetStyle = isMobile ? store.mobileFooterStyle : store.footerStyle;
  targetStyle.progressBarStyle[property] = value;
  store.anyUnsavedChanges = true;
};

const handleButtonStyle = (property, value) => {
  const isMobile = store.deviceMode === "mobile";
  const targetStyle = isMobile ? store.mobileFooterStyle : store.footerStyle;

  targetStyle.buttonStyle[property] = value;
  store.anyUnsavedChanges = true;
};

const handleMobileSubmitText = () => {
  if (store.deviceMode === "mobile") {
    store.mobileFooterStyle.buttonStyle.submitBtnText = mobileSubmitBtnText.value;
    store.anyUnsavedChanges = true;
    store.addToHistory();
  }
};

const prevBtnTextModel = computed({
  get() {
    return store.footerStyle.buttonStyle.prevBtnText;
  },
  set(value: string) {
    store.footerStyle.buttonStyle.prevBtnText = value;
    store.anyUnsavedChanges = true;
  },
});

const nextBtnTextModel = computed({
  get() {
    return store.footerStyle.buttonStyle.nextBtnText;
  },
  set(value: string) {
    store.footerStyle.buttonStyle.nextBtnText = value;
    store.anyUnsavedChanges = true;
  },
});

const submitBtnTextModel = computed({
  get() {
    return store.footerStyle.buttonStyle.submitBtnText;
  },
  set(value: string) {
    store.footerStyle.buttonStyle.submitBtnText = value;
    store.anyUnsavedChanges = true;
  },
});

const buttonFormValue = ref({
  prevBtnText: prevBtnTextModel.value,
  nextBtnText: nextBtnTextModel.value,
  submitBtnText: submitBtnTextModel.value,
});

const root = ref();
const rootMobile = ref();

const validateRequiredText = (value: string, model: Ref<string>) => {
  if (!(value && value.trim() !== "")) {
    return new Error(t("common.validation.fieldRequired"));
  }
  if (value.trim().length > 16) {
    return new Error(t("common.validation.maxLengthExceeded", { max: 16 }));
  }
  model.value = value.trim();
  return true;
};

const validateMobileSubmitText = (value: string) => {
  if (!(value && value.trim() !== "")) {
    return new Error(t("common.validation.fieldRequired"));
  }
  if (value.trim().length > 16) {
    return new Error(t("common.validation.maxLengthExceeded", { max: 16 }));
  }
  return true;
};

const rules = {
  prevBtnText: [
    {
      required: true,
      validator: (rule: any, value: string) => validateRequiredText(value, prevBtnTextModel),
    },
  ],
  nextBtnText: [
    {
      required: true,
      validator: (rule: any, value: string) => validateRequiredText(value, nextBtnTextModel),
    },
  ],
  submitBtnText: [
    {
      required: true,
      validator: (rule: any, value: string) => validateRequiredText(value, submitBtnTextModel),
    },
  ],
};

const mobileRules = {
  submitBtnText: [
    {
      required: true,
      validator: (rule: any, value: string) => validateMobileSubmitText(value),
    },
  ],
};

watch(
  buttonFormValue,
  async () => {
    try {
      await root.value.getForm().validate();
      store.enableSaveButton = true;
    } catch (error) {
      store.enableSaveButton = false;
    }
  },
  { deep: true }
);

watch(store?.footerStyle?.buttonStyle, (val) => {
  buttonFormValue.value.prevBtnText = val.prevBtnText;
  buttonFormValue.value.nextBtnText = val.nextBtnText;
  buttonFormValue.value.submitBtnText = val.submitBtnText;
});

// Watch for device mode changes to update mobile submit text
watch(
  () => store.deviceMode,
  (mode) => {
    if (mode === "mobile") {
      mobileSubmitBtnText.value =
        store.mobileFooterStyle?.buttonStyle?.submitBtnText || t("survey.footer.placeholders.submit");
    }
  }
);
</script>

<style scoped>
.footer-card-saperator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  flex: 1 0 0;
}

.footer-card {
  display: flex;
  height: 100px;
  width: 140px;
  justify-content: center;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  border-radius: 8px;
  border: 2px solid #eaecf0;
  background: #fff;
}

.footer-card-active {
  border: 2px solid #155eef;
}

.right-zero {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.n-form-item.n-form-item--top-labelled {
  grid-template-rows: auto;
}

.form-item {
  display: flex;
  flex-direction: column;
  justify-items: flex-start;
  align-items: center;
}

.active {
  border: 2px solid #155eef !important;
  background: #eff4ff;
}
</style>
